import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { ArtistScrapeStatus } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { SMLCardSparkline } from '../smlListItemBox/SMLCardSparkline';
import { ConnectionInsight } from '../../../redux/api/fymAPI.types';
import { followerSparklineValues } from './followerSparkline';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectionFollowersProps {
  insight: ConnectionInsight;
}

/**
 * The follower line on a connection card: the count, the week's movement and
 * the shape of the last months.
 *
 * Apple Music and TIDAL publish no follower count, so the whole series is null
 * there. That draws nothing at all — "0 fans" would be a number we invented.
 */
const ConnectionFollowersComponent: React.FC<ConnectionFollowersProps> = ({ insight }) => {
  const intl = useIntl();
  const { followers, status } = insight;
  const values = useMemo(() => followerSparklineValues(followers.series), [followers.series]);

  if (followers.latest === null) {
    return null;
  }

  return (
    <div className={styles.followers}>
      <span className={styles.followerCount}>
        {intl.formatMessage(
          { id: 'app.sml.catalog.insights.followers', defaultMessage: '{count} followers' },
          { count: intl.formatNumber(followers.latest) }
        )}
      </span>
      {followers.weekDelta !== null && (
        <span className={clsx(styles.followerDelta, followers.weekDelta < 0 && styles.followerDeltaDown)}>
          {intl.formatMessage(
            { id: 'app.sml.catalog.insights.week_delta', defaultMessage: '{delta} this week' },
            { delta: intl.formatNumber(followers.weekDelta, { signDisplay: 'exceptZero' }) }
          )}
        </span>
      )}
      <SMLCardSparkline values={values} />
      {status === ArtistScrapeStatus.failing && (
        <span className={styles.followerStale}>
          {intl.formatMessage({
            id: 'app.sml.catalog.insights.stale',
            defaultMessage: 'The last few checks did not get through, so this number may be a few days old.',
          })}
        </span>
      )}
    </div>
  );
};

export const ConnectionFollowers = TypedMemo(ConnectionFollowersComponent);
